import { useState, useEffect } from 'react';
import { getData } from '../../api/ApiRest';
import LoadingComponent from './../../widgets/LoadingComponent';
import { NumericFormat } from 'react-number-format';
import { FaCheckCircle } from 'react-icons/fa';
import { RiAlertFill } from 'react-icons/ri';
import {
    Box, Paper, Grid, Typography, Chip, Tooltip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, IconButton
} from '@mui/material';
import { MdClose } from 'react-icons/md';
import { styled } from '@mui/material/styles';

// ── Estilos de tabla ──────────────────────────────────────────────────────────

const SummaryHeaderCell = styled(TableCell)(() => ({
    backgroundColor: '#224B6A',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '0.78rem',
    padding: '8px 12px',
}));

const SummaryCell = styled(TableCell)(() => ({
    fontSize: '0.82rem',
    fontWeight: 600,
    padding: '8px 12px',
}));

const DetailHeaderCell = styled(TableCell)(() => ({
    backgroundColor: '#224B6A',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '0.73rem',
    whiteSpace: 'nowrap',
    padding: '8px 10px',
}));

const DetailCell = styled(TableCell)(() => ({
    fontSize: '0.73rem',
    padding: '5px 10px',
    borderColor: '#dee2e6',
}));

// ── Colores por rango de tiempo ───────────────────────────────────────────────

const RANGOS = [
    { label: 'Óptimo',    rango: '0 – 10 s',  color: '#166534', bg: '#dcfce7', min: 0,  max: 10  },
    { label: 'Bueno',     rango: '11 – 20 s', color: '#1e40af', bg: '#dbeafe', min: 11, max: 20  },
    { label: 'Aceptable', rango: '21 – 40 s', color: '#92400e', bg: '#fef9c3', min: 21, max: 40  },
    { label: 'Lento',     rango: '41 s +',    color: '#991b1b', bg: '#fee2e2', min: 41, max: Infinity },
];

const rowBgByDuracion = (seg) => {
    if (seg >= 41) return '#fff5f5';
    if (seg >= 21) return '#fffdf0';
    if (seg >= 11) return '#f0f5ff';
    return '#f0fdf4';
};

// ── Componente tabla de resumen genérica ──────────────────────────────────────

function ResumenTable({ title, rows }) {
    return (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ px: 2, py: 1.2, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#224B6A" fontSize="0.82rem">
                    {title}
                </Typography>
            </Box>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {rows.headers.map((h, i) => (
                                <SummaryHeaderCell key={i}>{h}</SummaryHeaderCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.data.map((row, i) => (
                            <TableRow key={i} sx={{ backgroundColor: row.bg, '&:hover': { filter: 'brightness(0.97)' } }}>
                                {row.cells.map((cell, j) => (
                                    <SummaryCell key={j} sx={{ color: row.color }}>{cell}</SummaryCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

// ── Componente principal ──────────────────────────────────────────────────────

function EmisionTiempo() {
    const [loading, setLoading] = useState(false);
    const [registrosEmision, setRegistrosEmision] = useState([]);
    const [modalError, setModalError] = useState({ Visible: false, Valor: {} });

    useEffect(() => {
        setLoading(true);
        getData('api/Configuracion/Reporte/GetReporteEmisionTiempo', {}, false)
            .then(response => {
                setRegistrosEmision(response.documents);
                setLoading(false);
            }, () => setLoading(false));
    }, []);

    const conteo = (min, max = Infinity) =>
        registrosEmision.filter(item => item.duracion_Seg >= min && item.duracion_Seg <= max).length;

    const pct = (n) => (n / registrosEmision.length * 100) || 0;

    const verTextoModal = (item) => {
        setModalError({ Visible: true, Valor: JSON.parse(item.resutado_Text) });
    };

    // ── Datos para tablas de resumen ─────────────────────────────────────────

    const tiemposRows = {
        headers: ['Rango', 'Duración', 'Cantidad', '%'],
        data: RANGOS.map(r => {
            const cant = conteo(r.min, r.max);
            return {
                bg: r.bg, color: r.color,
                cells: [
                    r.label, r.rango, cant,
                    <NumericFormat key="pct" displayType="text" thousandSeparator="," decimalScale={2} value={pct(cant)} suffix="%" />
                ]
            };
        })
    };

    const totalDocs = registrosEmision.length;
    const exitosos = registrosEmision.filter(i => i.resultado === true).length;
    const errores = totalDocs - exitosos;

    const estadoRows = {
        headers: ['Estado', 'Cantidad', '%'],
        data: [
            {
                bg: '#dcfce7', color: '#166534',
                cells: ['Éxito', exitosos,
                    <NumericFormat key="p" displayType="text" decimalScale={2} value={pct(exitosos)} suffix="%" />]
            },
            {
                bg: '#fee2e2', color: '#991b1b',
                cells: ['Error', errores,
                    <NumericFormat key="p" displayType="text" decimalScale={2} value={pct(errores)} suffix="%" />]
            },
        ]
    };

    const pos = registrosEmision.filter(i => i.tipo === 'POS').length;
    const fv = totalDocs - pos;

    const tipoRows = {
        headers: ['Tipo', 'Cantidad', '%'],
        data: [
            {
                bg: '#dbeafe', color: '#1e40af',
                cells: ['POS', pos,
                    <NumericFormat key="p" displayType="text" decimalScale={2} value={pct(pos)} suffix="%" />]
            },
            {
                bg: '#f1f5f9', color: '#475569',
                cells: ['FV', fv,
                    <NumericFormat key="p" displayType="text" decimalScale={2} value={pct(fv)} suffix="%" />]
            },
        ]
    };

    return (
        <Box sx={{ p: 2 }}>
            <LoadingComponent Loading={loading} />

            <Typography variant="h6" fontWeight={700} color="#224B6A"
                align="center" sx={{ mb: 3, letterSpacing: 0.5 }}>
                Reporte de Tiempos de Emisión
            </Typography>

            {/* Resumen */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={8}>
                    <ResumenTable title="Distribución por tiempo de respuesta" rows={tiemposRows} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ResumenTable title="Estado de emisión" rows={estadoRows} />
                    <ResumenTable title="Tipo de documento" rows={tipoRows} />
                </Grid>
            </Grid>

            {/* Tabla de detalle */}
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <DetailHeaderCell>#</DetailHeaderCell>
                            <DetailHeaderCell>Empresa</DetailHeaderCell>
                            <DetailHeaderCell align="center">Documento</DetailHeaderCell>
                            <DetailHeaderCell align="center">Tipo</DetailHeaderCell>
                            <DetailHeaderCell align="center">Detalles</DetailHeaderCell>
                            <DetailHeaderCell align="center">Inicio</DetailHeaderCell>
                            <DetailHeaderCell align="center">Inicio Obj. TAE</DetailHeaderCell>
                            <DetailHeaderCell align="center">Fin Obj. TAE</DetailHeaderCell>
                            <DetailHeaderCell align="center">Seg. TAE</DetailHeaderCell>
                            <DetailHeaderCell align="center">Inicio API DIAN</DetailHeaderCell>
                            <DetailHeaderCell align="center">Fin API DIAN</DetailHeaderCell>
                            <DetailHeaderCell align="center">Seg. DIAN</DetailHeaderCell>
                            <DetailHeaderCell align="center">Total Seg.</DetailHeaderCell>
                            <DetailHeaderCell align="center">Resultado</DetailHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {registrosEmision.length === 0 ? (
                            <TableRow>
                                <DetailCell colSpan={14} align="center" sx={{ color: '#9ca3af', py: 4 }}>
                                    Sin datos disponibles
                                </DetailCell>
                            </TableRow>
                        ) : (
                            registrosEmision.map((item, index) => (
                                <TableRow key={index}
                                    sx={{
                                        backgroundColor: rowBgByDuracion(item.duracion_Seg),
                                        '&:hover': { filter: 'brightness(0.96)' }
                                    }}>
                                    <DetailCell>{index + 1}</DetailCell>
                                    <DetailCell>{item.company_Nit} - {item.company}</DetailCell>
                                    <DetailCell align="center">{item.prefijo} - {item.numero}</DetailCell>
                                    <DetailCell align="center">{item.tipo}</DetailCell>
                                    <DetailCell align="center">{item.detalles}</DetailCell>
                                    <DetailCell align="center">{item.peticion_Inicio_}</DetailCell>
                                    <DetailCell align="center">{item.peticion_Inicio_ObjetoTae_}</DetailCell>
                                    <DetailCell align="center">{item.peticion_Fin_ObjetoTae_}</DetailCell>
                                    <DetailCell align="center">{item.peticion_ObjetoTae}</DetailCell>
                                    <DetailCell align="center">{item.peticion_Inicio_Apidian_}</DetailCell>
                                    <DetailCell align="center">{item.peticion_Fin_Apidian_}</DetailCell>
                                    <DetailCell align="center">{item.peticion_Apidian}</DetailCell>
                                    <DetailCell align="center">
                                        <Chip
                                            label={`${item.duracion_Seg}s`}
                                            size="small"
                                            sx={{
                                                fontSize: '0.7rem', fontWeight: 700,
                                                backgroundColor: rowBgByDuracion(item.duracion_Seg),
                                                border: '1px solid currentColor',
                                                color: item.duracion_Seg >= 41 ? '#991b1b'
                                                    : item.duracion_Seg >= 21 ? '#92400e'
                                                    : item.duracion_Seg >= 11 ? '#1e40af'
                                                    : '#166534'
                                            }}
                                        />
                                    </DetailCell>
                                    <DetailCell align="center">
                                        {item.resultado === true ? (
                                            <Tooltip title="Emitido con éxito">
                                                <span><FaCheckCircle color="#16a34a" size={15} /></span>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Ver error">
                                                <span>
                                                    <RiAlertFill size={15} color="#dc2626"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => verTextoModal(item)} />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </DetailCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Modal error */}
            <Dialog open={modalError.Visible} onClose={() => setModalError({ Visible: false, Valor: {} })}
                maxWidth="lg" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#224B6A', color: '#fff', py: 1.5, px: 2 }}>
                    <Typography fontWeight={700} fontSize="0.95rem">Error de Emisión</Typography>
                    <IconButton size="small" onClick={() => setModalError({ Visible: false, Valor: {} })}
                        sx={{ color: '#fff' }}>
                        <MdClose size={18} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {modalError.Visible && (
                        <Box component="pre"
                            sx={{
                                m: 0, p: 3,
                                fontSize: '0.78rem',
                                backgroundColor: '#1e1e1e',
                                color: '#d4d4d4',
                                overflowX: 'auto',
                                minHeight: 200,
                            }}>
                            {JSON.stringify(JSON.parse(modalError.Valor), null, 2)}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default EmisionTiempo;
